import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  Invitation,
  CreateInvitationRequest,
  InvitationFilters,
  PaginatedResponse,
} from '@/types'

interface ApiResponse<T> {
  message?: string
  data?: T
}

const BASE_URL = API_ENDPOINTS.INVITATIONS

interface RawPaginatedResponse {
  data?: Invitation[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

function normalizePaginatedInvitations(raw: RawPaginatedResponse): PaginatedResponse<Invitation> {
  const data = Array.isArray(raw?.data) ? raw.data : []
  const meta = raw?.meta || {}
  return {
    data,
    current_page: meta.current_page ?? raw.current_page ?? 1,
    last_page: meta.last_page ?? raw.last_page ?? 1,
    per_page: meta.per_page ?? raw.per_page ?? 20,
    total: meta.total ?? raw.total ?? 0,
  }
}

export const invitationsApi = {
  getInvitations: async (
    filters?: InvitationFilters
  ): Promise<PaginatedResponse<Invitation>> => {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.search) params.append('search', filters.search)
    if (filters?.page) params.append('page', String(filters.page))
    if (filters?.per_page) params.append('per_page', String(filters.per_page))
    const query = params.toString()
    const url = query ? `${BASE_URL}?${query}` : BASE_URL
    const response = await apiClient.get<RawPaginatedResponse>(url)
    return normalizePaginatedInvitations(response.data)
  },

  createInvitation: async (
    data: CreateInvitationRequest
  ): Promise<Invitation> => {
    const response = await apiClient.post<ApiResponse<Invitation>>(BASE_URL, data)
    if (response.data?.data) {
      return response.data.data
    }
    throw new Error(
      response.data?.message || 'Ошибка создания приглашения'
    )
  },

  resendInvitation: async (id: number): Promise<Invitation> => {
    const response = await apiClient.post<ApiResponse<Invitation>>(
      `${BASE_URL}/${id}/resend`
    )
    if (response.data?.data) {
      return response.data.data
    }
    throw new Error(
      response.data?.message || 'Ошибка повторной отправки'
    )
  },

  deleteInvitation: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`)
  },
}
